import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lock, FileText, Shield, AlertTriangle } from "lucide-react";

const CONTACT_EMAIL = "contact@skillswipe.co.in";
const LAST_UPDATED = "December 8, 2025";

export const PrivacyPolicyDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
        <Lock className="h-4 w-4" />
        Privacy Policy
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle>Privacy Policy</DialogTitle>
        <DialogDescription>Last updated: {LAST_UPDATED}</DialogDescription>
      </DialogHeader>
      <ScrollArea className="h-[60vh] pr-4">
        <div className="space-y-6 text-sm">
          <section>
            <h4 className="font-semibold mb-2">1. Introduction</h4>
            <p className="text-muted-foreground">
              Welcome to SkillSwipe ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use it, and what rights you have in relation to it.
            </p>
            <p className="text-muted-foreground mt-2">
              By using SkillSwipe, you agree to the collection and use of information in accordance with this policy. If you have any questions, please contact us at {CONTACT_EMAIL}.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">2. Information We Collect</h4>
            <p className="text-muted-foreground mb-2">We collect the following types of information:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Personal Information:</strong> Name, email address, profile photos, gender, location, and any other information you voluntarily provide during registration or profile updates.</li>
              <li><strong>Skills Data:</strong> Information about skills you can teach and skills you want to learn, including custom skills you create.</li>
              <li><strong>Usage Data:</strong> Information about how you use our app, including session history, ratings given and received, showcase posts, challenge participations, and interaction patterns.</li>
              <li><strong>Communication Data:</strong> Messages exchanged with other users through our chat feature, voice messages, and any attachments shared.</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address, and unique device identifiers.</li>
              <li><strong>Media Content:</strong> Photos, videos (up to 30 seconds), and screenshots uploaded to showcase posts, profile galleries, or skill verifications.</li>
              <li><strong>Social Media Information:</strong> Links to your social media profiles (Facebook, Instagram, Twitter, LinkedIn, Snapchat) if you choose to provide them.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">3. How We Use Your Information</h4>
            <p className="text-muted-foreground mb-2">We use collected information for the following purposes:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Service Provision:</strong> To create and manage your account, match you with skill partners, facilitate skill exchange sessions, and enable communication between users.</li>
              <li><strong>SkillSwipe Score Calculation:</strong> To calculate and display your SkillSwipe Score based on completed sessions, ratings received, skills taught, achievements earned, and showcase impressions.</li>
              <li><strong>Personalization:</strong> To personalize your experience, provide skill recommendations, and suggest relevant learning partners based on your preferences and activity.</li>
              <li><strong>Communication:</strong> To send you service-related notifications, updates, security alerts, and administrative messages.</li>
              <li><strong>Safety and Security:</strong> To detect, prevent, and address fraud, abuse, security risks, and technical issues.</li>
              <li><strong>Analytics:</strong> To understand how users interact with our service, improve our features, and develop new functionality.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">4. Information Sharing and Disclosure</h4>
            <p className="text-muted-foreground mb-2">We may share your information in the following circumstances:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>With Other Users:</strong> Your profile information, skills, showcase posts, and public content are visible to other SkillSwipe users. Private messages are only visible to conversation participants.</li>
              <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf (hosting, analytics, email delivery).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, subpoena, court order, or government request.</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets, user information may be transferred to the acquiring entity.</li>
              <li><strong>With Your Consent:</strong> We may share information for other purposes with your explicit consent.</li>
            </ul>
            <p className="text-muted-foreground mt-2 font-semibold">
              We do NOT sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">5. Data Retention</h4>
            <p className="text-muted-foreground">
              We retain your personal information for as long as your account is active or as needed to provide you services. When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal, accounting, or security purposes. Some information (such as messages sent to other users) may remain visible to those users even after account deletion.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">6. Data Security</h4>
            <p className="text-muted-foreground">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, and regular security assessments. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">7. Your Rights and Choices</h4>
            <p className="text-muted-foreground mb-2">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Access:</strong> You can access and review your personal information through your profile settings.</li>
              <li><strong>Correction:</strong> You can update or correct your information at any time through the app.</li>
              <li><strong>Deletion:</strong> You can delete your account and all associated data through the app settings.</li>
              <li><strong>Portability:</strong> You can request a copy of your data in a portable format.</li>
              <li><strong>Opt-out:</strong> You can opt out of promotional communications while still receiving service-related notifications.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              To exercise these rights, contact us at {CONTACT_EMAIL}.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">8. Children's Privacy</h4>
            <p className="text-muted-foreground">
              SkillSwipe is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information promptly. If you believe we have collected information from a child under 13, please contact us at {CONTACT_EMAIL}.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">9. International Data Transfers</h4>
            <p className="text-muted-foreground">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country. We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">10. Changes to This Policy</h4>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of SkillSwipe after any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">11. Contact Us</h4>
            <p className="text-muted-foreground">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              <strong>Email:</strong> {CONTACT_EMAIL}<br />
              <strong>Subject:</strong> Privacy Policy Inquiry
            </p>
          </section>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

export const TermsOfServiceDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
        <FileText className="h-4 w-4" />
        Terms of Service
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle>Terms of Service</DialogTitle>
        <DialogDescription>Last updated: {LAST_UPDATED}</DialogDescription>
      </DialogHeader>
      <ScrollArea className="h-[60vh] pr-4">
        <div className="space-y-6 text-sm">
          <section>
            <h4 className="font-semibold mb-2">1. Acceptance of Terms</h4>
            <p className="text-muted-foreground">
              By accessing or using SkillSwipe ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">2. Description of Service</h4>
            <p className="text-muted-foreground">
              SkillSwipe is a peer-to-peer skill exchange platform that connects users who want to teach and learn skills from each other. The Service facilitates skill matching, video sessions, messaging, showcasing skills, participating in challenges, and building learning communities. SkillSwipe provides the platform and technology but is not responsible for the quality, accuracy, or outcomes of skill exchanges between users.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">3. User Accounts</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>You must be at least 13 years old to use the Service. If you are under 18, you must have parental or guardian consent.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</li>
              <li>You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.</li>
              <li>You may not use another person's account without permission or create accounts using false or misleading information.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms or are inactive for extended periods.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">4. User Conduct</h4>
            <p className="text-muted-foreground mb-2">You agree to use the Service responsibly and lawfully. You must NOT:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Violate any applicable laws, regulations, or third-party rights.</li>
              <li>Post false, misleading, defamatory, or fraudulent content.</li>
              <li>Harass, threaten, intimidate, stalk, or bully other users.</li>
              <li>Share sexually explicit, violent, or otherwise objectionable content.</li>
              <li>Impersonate another person or entity or misrepresent your affiliation.</li>
              <li>Spam, solicit, or advertise products or services not related to skill exchange.</li>
              <li>Attempt to gain unauthorized access to other users' accounts or our systems.</li>
              <li>Use automated means (bots, scrapers) to access the Service without permission.</li>
              <li>Interfere with or disrupt the Service or servers/networks connected to it.</li>
              <li>Collect or harvest user data without consent.</li>
              <li>Use the Service for any illegal, harmful, or unauthorized purpose.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">5. Content and Intellectual Property</h4>
            <p className="text-muted-foreground mb-2"><strong>Your Content:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>You retain ownership of content you create and share on SkillSwipe (profile information, posts, messages, videos).</li>
              <li>By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, copy, modify, and display your content in connection with the Service.</li>
              <li>You represent that you have all necessary rights to share any content you post.</li>
            </ul>
            <p className="text-muted-foreground mt-3 mb-2"><strong>Our Content:</strong></p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>The Service and its original content (excluding user content), features, and functionality are owned by SkillSwipe and protected by intellectual property laws.</li>
              <li>You may not copy, modify, distribute, sell, or lease any part of the Service without explicit written permission.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">6. Skill Exchange Sessions</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>SkillSwipe facilitates connections between users but is not a party to any skill exchange arrangement.</li>
              <li>Users are solely responsible for the content, quality, and safety of their skill exchange sessions.</li>
              <li>Session completion requires mutual confirmation (ratings) from both participants.</li>
              <li>Video sessions are conducted through integrated third-party services (Jitsi Meet). Use of these services is subject to their respective terms.</li>
              <li>We do not guarantee the accuracy of skill claims or the quality of instruction provided by users.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">7. SkillSwipe Score and Achievements</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>The SkillSwipe Score is a gamification feature based on user activity (sessions, ratings, teaching, achievements, impressions).</li>
              <li>Scores are calculated automatically based on platform activity and may be adjusted to maintain fairness.</li>
              <li>We reserve the right to modify the scoring system at any time without prior notice.</li>
              <li>Manipulation of scores or achievements through fraudulent activity will result in account termination.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">8. Ratings and Reviews</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Users can rate each other after completing skill exchange sessions (1-5 stars).</li>
              <li>Ratings must be honest, accurate, and based on your actual experience.</li>
              <li>Rating manipulation, coercion, or exchange of ratings for benefits is prohibited.</li>
              <li>Once submitted, ratings are final but can be edited. You cannot give multiple ratings to the same profile for the same session.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">9. Third-Party Services</h4>
            <p className="text-muted-foreground">
              The Service may contain links to or integrate with third-party websites, services, or applications (video calling, analytics, authentication). We are not responsible for the content, privacy policies, or practices of third parties. Your use of third-party services is at your own risk and subject to their terms.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">10. Termination</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>You may terminate your account at any time through the app settings.</li>
              <li>We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms.</li>
              <li>Upon termination, your right to use the Service ceases immediately. Provisions that should survive termination (intellectual property, disclaimers, limitations of liability) will survive.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">11. Modifications to Terms</h4>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the modified Terms. If you disagree with changes, you should stop using the Service.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">12. Governing Law</h4>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law provisions. Any disputes arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts in India.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">13. Contact Us</h4>
            <p className="text-muted-foreground">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              <strong>Email:</strong> {CONTACT_EMAIL}<br />
              <strong>Subject:</strong> Terms of Service Inquiry
            </p>
          </section>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

export const UserResponsibilitiesDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
        <Shield className="h-4 w-4" />
        User Responsibilities
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle>User Responsibilities</DialogTitle>
        <DialogDescription>Guidelines for being a responsible SkillSwipe user</DialogDescription>
      </DialogHeader>
      <ScrollArea className="h-[60vh] pr-4">
        <div className="space-y-6 text-sm">
          <section>
            <h4 className="font-semibold mb-2">1. Profile Accuracy</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Provide accurate and truthful information about yourself, including your name, skills, and qualifications.</li>
              <li>Only list skills you can genuinely teach at a competent level.</li>
              <li>Keep your profile information up to date.</li>
              <li>Use a clear, appropriate profile photo that represents you.</li>
              <li>Do not impersonate others or misrepresent your expertise.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">2. Skill Exchange Sessions</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Honor your commitments to scheduled sessions. If you must cancel, inform your partner as early as possible.</li>
              <li>Be punctual and prepared for your sessions.</li>
              <li>Provide quality instruction when teaching and be an engaged learner when learning.</li>
              <li>Agree on session duration and topics with your partner beforehand.</li>
              <li>Complete the post-session rating to help improve the community.</li>
              <li>Provide honest and constructive feedback in your ratings.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">3. Communication Etiquette</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Communicate respectfully and professionally with all users.</li>
              <li>Respond to messages in a timely manner when possible.</li>
              <li>Keep conversations focused on skill exchange and learning.</li>
              <li>Do not send unsolicited promotional content or spam.</li>
              <li>Avoid sharing personal contact information until you trust the other person.</li>
              <li>Report any inappropriate, harassing, or threatening messages immediately.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">4. Content Standards</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Only post content that you have the right to share.</li>
              <li>Ensure showcase videos and posts are relevant to skill demonstration.</li>
              <li>Do not post inappropriate, offensive, or harmful content.</li>
              <li>Respect copyright and intellectual property rights of others.</li>
              <li>Obtain consent before sharing images or videos of other people.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">5. Safety and Security</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Protect your account credentials and never share your password.</li>
              <li>Be cautious when sharing personal information with other users.</li>
              <li>Report suspicious activity, scams, or security concerns immediately.</li>
              <li>Trust your instincts—if something feels wrong, disengage and report.</li>
              <li>For in-person meetups (if any), take appropriate safety precautions.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">6. Fair Play</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Do not manipulate ratings, scores, or achievements through fake accounts or collusion.</li>
              <li>Participate in challenges fairly and submit genuine work.</li>
              <li>Do not use bots or automated tools to interact with the platform.</li>
              <li>Give credit where it's due when sharing knowledge learned from others.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">7. Community Building</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Contribute positively to the SkillSwipe community.</li>
              <li>Help newcomers learn how to use the platform.</li>
              <li>Participate in learning circles and group activities constructively.</li>
              <li>Share success stories to inspire and motivate others.</li>
              <li>Provide feedback to help us improve the platform.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">8. Compliance</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Follow all applicable local, national, and international laws.</li>
              <li>Adhere to these responsibilities and our Terms of Service.</li>
              <li>Cooperate with any investigations into violations.</li>
              <li>Accept consequences (warnings, suspensions, terminations) for violations.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Questions?</h4>
            <p className="text-muted-foreground">
              If you have questions about your responsibilities or need to report a concern, contact us at {CONTACT_EMAIL}.
            </p>
          </section>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

export const DisclaimersDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
        <AlertTriangle className="h-4 w-4" />
        Disclaimers
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle>Disclaimers</DialogTitle>
        <DialogDescription>Important legal notices and limitations</DialogDescription>
      </DialogHeader>
      <ScrollArea className="h-[60vh] pr-4">
        <div className="space-y-6 text-sm">
          <section>
            <h4 className="font-semibold mb-2">1. Service Disclaimer</h4>
            <p className="text-muted-foreground">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
            <p className="text-muted-foreground mt-2">
              SkillSwipe, its subsidiaries, affiliates, and licensors do not warrant that: (a) the Service will function uninterrupted, secure, or available at any particular time or location; (b) any errors or defects will be corrected; (c) the Service is free of viruses or other harmful components; or (d) the results of using the Service will meet your requirements.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">2. User Content Disclaimer</h4>
            <p className="text-muted-foreground">
              SkillSwipe does not control, verify, or endorse user-generated content, including but not limited to profiles, skill claims, messages, showcase posts, challenge submissions, and video sessions. We are not responsible for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>The accuracy, completeness, or reliability of any user content.</li>
              <li>The quality of skills taught or instruction provided by users.</li>
              <li>Any outcomes, learning results, or consequences from skill exchanges.</li>
              <li>Any content that may be offensive, inappropriate, or objectionable.</li>
              <li>Any errors, omissions, or misrepresentations in user profiles or claims.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">3. No Professional Advice</h4>
            <p className="text-muted-foreground">
              Content and skills shared on SkillSwipe are for general informational and educational purposes only. They do not constitute professional advice (legal, medical, financial, or otherwise). Always seek the advice of qualified professionals for specific concerns.
            </p>
            <p className="text-muted-foreground mt-2">
              Do not rely on skills learned through SkillSwipe for professional certifications, licensing requirements, or safety-critical applications without proper verification and training from accredited institutions.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">4. Third-Party Disclaimer</h4>
            <p className="text-muted-foreground">
              SkillSwipe may contain links to or integrate with third-party websites, services, or applications (including video conferencing services, authentication providers, and analytics tools). We do not endorse, control, or assume responsibility for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>The content, privacy policies, or practices of third-party services.</li>
              <li>Any products or services offered by third parties.</li>
              <li>Any damages or losses caused by third-party services.</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              Your use of third-party services is at your sole risk and subject to their respective terms.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">5. Limitation of Liability</h4>
            <p className="text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SKILLSWIPE, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>Loss of profits, data, use, goodwill, or other intangible losses.</li>
              <li>Damages resulting from unauthorized access to or use of our servers or personal information.</li>
              <li>Damages resulting from interruption or cessation of transmission to or from the Service.</li>
              <li>Damages resulting from bugs, viruses, or other harmful code transmitted through the Service.</li>
              <li>Damages resulting from the conduct or content of any third party on the Service.</li>
              <li>Damages arising from any skill exchange, session, or interaction between users.</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              IN NO EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE AMOUNT YOU PAID US, IF ANY, IN THE PAST SIX MONTHS FOR THE SERVICES GIVING RISE TO THE CLAIM.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">6. Indemnification</h4>
            <p className="text-muted-foreground">
              You agree to defend, indemnify, and hold harmless SkillSwipe and its licensors, employees, contractors, agents, officers, and directors from and against any claims, damages, obligations, losses, liabilities, costs, or debt arising from:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>Your use of and access to the Service.</li>
              <li>Your violation of these Terms or any applicable law.</li>
              <li>Your violation of any third-party rights, including intellectual property or privacy rights.</li>
              <li>Any content you post or share on the Service.</li>
              <li>Any skill exchange or interaction with other users.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">7. No Guarantee of Matches or Outcomes</h4>
            <p className="text-muted-foreground">
              SkillSwipe does not guarantee:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>That you will find skill partners or matches.</li>
              <li>The number, quality, or frequency of matches.</li>
              <li>That you will learn specific skills or achieve particular outcomes.</li>
              <li>That skill exchanges will be successful or satisfying.</li>
              <li>Specific levels of SkillSwipe Score or achievements.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">8. Accuracy of Information</h4>
            <p className="text-muted-foreground">
              While we strive to provide accurate information, we do not warrant that the Service's content is accurate, complete, or current. We may make changes to the Service, features, and content at any time without notice.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">9. Risk Acknowledgment</h4>
            <p className="text-muted-foreground">
              By using SkillSwipe, you acknowledge that:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2 mt-2">
              <li>Interacting with strangers online carries inherent risks.</li>
              <li>You are solely responsible for your interactions with other users.</li>
              <li>You assume full responsibility for your safety and security.</li>
              <li>SkillSwipe is not responsible for the conduct of any user, whether online or offline.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Contact Us</h4>
            <p className="text-muted-foreground">
              If you have questions about these disclaimers, please contact us at {CONTACT_EMAIL}.
            </p>
          </section>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>
);
